/* Per-client lifecycle for the live Wikimedia facts plane.
 *
 * The workroom render is synchronous (cockpit paint()), so this module owns the
 * async fetch: `ensureWikipedia` kicks off exactly one connector fetch per
 * (slug, article), returns the current snapshot, and calls back once when the
 * fetch settles so the cockpit re-paints. State is reset on cockpit teardown so
 * client A's Wikipedia never bleeds into client B.
 */
import type { AppPayloadState } from '../../derive/state-shape.ts';
import type { NormalizedAsset } from '../../types/contract.ts';
import { fetchClientWikipedia, type WpRecord } from '../../api/wikipedia.ts';

export type ConnectorStatus = 'idle' | 'loading' | 'ok' | 'error';

export interface ConnectorState {
  status: ConnectorStatus;
  article: string | null;
  record: WpRecord | null;
  reason: string | null;
}

interface Entry extends ConnectorState {
  key: string;
}

const _bySlug = new Map<string, Entry>();

const IDLE: ConnectorState = { status: 'idle', article: null, record: null, reason: null };

/** The Wikipedia article URL the connector should fetch for this client: the
 * client's OWN core article (the primary brand entity), preferring the English
 * edition. Ranks by core-entity flag, then global scope, then hub role, so a
 * subsidiary/product mention (e.g. "Up (airline)") never wins over the brand's
 * own article. Returns null when the client has no Wikipedia asset — the
 * workroom then renders its honest "no article mapped" state, never a fabricated
 * fetch. */
export function primaryWikiArticleUrl(state: AppPayloadState): string | null {
  const isArticle = (url: string | null | undefined): boolean => {
    if (!url) return false;
    try {
      const u = new URL(url);
      return /\.wikipedia\.org$/.test(u.hostname.toLowerCase()) && u.pathname.startsWith('/wiki/');
    } catch {
      return false;
    }
  };
  const isEnglish = (url: string): boolean => {
    try {
      return new URL(url).hostname.toLowerCase() === 'en.wikipedia.org';
    } catch {
      return false;
    }
  };
  const wikis = state.assets.filter(
    (a: NormalizedAsset) => (a.platform === 'Wikipedia' || a.assetType === 'wiki') && isArticle(a.url),
  );
  if (!wikis.length) return null;
  const score = (a: NormalizedAsset): number =>
    (a.isCoreEntity ? 8 : 0) +
    (isEnglish(a.url as string) ? 4 : 0) +
    (a.scope === 'global' ? 2 : 0) +
    (a.role === 'hub' ? 1 : 0);
  const best = [...wikis].sort((a, b) => score(b) - score(a))[0];
  return best?.url ?? null;
}

/** Snapshot of the connector state for this client, kicking off a single fetch
 * on first call for a given (slug, article). `onSettled` fires once when the
 * fetch resolves or fails, so the caller can re-paint. */
export function ensureWikipedia(
  slug: string,
  article: string | null,
  onSettled: () => void,
): ConnectorState {
  if (!article) return IDLE;
  const key = `${slug}::${article}`;
  const existing = _bySlug.get(slug);
  if (existing && existing.key === key) return existing;

  const entry: Entry = { key, status: 'loading', article, record: null, reason: null };
  _bySlug.set(slug, entry);

  // editions=0: the connector-backed panels (health/citations/edits/content)
  // are all primary-article; the Languages tab stays payload-driven. Fetching
  // language editions would multiply Wikimedia calls (each ~7s apart on the
  // unauthenticated rate) for data the workroom does not use here. talk=0: Talk
  // drafting is out of scope. A provisioned WIKIMEDIA_API_TOKEN makes even this
  // near-instant (5000 vs 500 req/hour).
  fetchClientWikipedia(slug, article, { revisions: 20, editions: 0, talk: 0 })
    .then((resp) => {
      // A newer article selection for this slug supersedes this response.
      if (_bySlug.get(slug)?.key !== key) return;
      if (resp.available && resp.wikipedia) {
        _bySlug.set(slug, { key, status: 'ok', article, record: resp.wikipedia, reason: null });
      } else {
        _bySlug.set(slug, {
          key,
          status: 'error',
          article,
          record: null,
          reason: resp.reason || 'Wikimedia returned no article facts.',
        });
      }
      onSettled();
    })
    .catch((err) => {
      if (_bySlug.get(slug)?.key !== key) return;
      _bySlug.set(slug, {
        key,
        status: 'error',
        article,
        record: null,
        reason: err instanceof Error ? err.message : 'Wikimedia request failed.',
      });
      onSettled();
    });

  return entry;
}

/** Clear connector state (cockpit teardown). */
export function resetWikiConnector(): void {
  _bySlug.clear();
}
